/*
 **	iOS-style List Item Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React from 'react'
import { OCAColors, Typography, Spacing, Animation } from '../../theme/colors'

interface IOSListItemProps {
  icon?: React.ReactNode
  title: string
  subtitle?: string | React.ReactNode
  value?: string
  status?: string
  statusColor?: string
  onClick?: () => void
  showChevron?: boolean
  paddingLeft?: string  // Custom left padding for indentation
  borderColor?: string  // Left border color for status indication
}

export const IOSListItem: React.FC<IOSListItemProps> = ({
  icon,
  title,
  subtitle,
  value,
  status,
  statusColor,
  onClick,
  showChevron = true,
  paddingLeft,
  borderColor
}) => {
  const isClickable = !!onClick
  const [isPressed, setIsPressed] = React.useState(false)
  const isAlarm = status === 'Alarm' || status === 'Alarm (Offline)'

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: `12px ${Spacing.lg}`,
        paddingLeft: paddingLeft || Spacing.lg,
        backgroundColor: isPressed ? OCAColors.backgroundSecondary : OCAColors.background,
        borderBottom: `0.5px solid ${OCAColors.separator}`,
        borderLeft: borderColor ? `4px solid ${borderColor}` : 'none',
        cursor: isClickable ? 'pointer' : 'default',
        minHeight: '44px', // Native iOS minimum touch target
        transform: isPressed ? Animation.scaleDown : 'scale(1)',
        transition: `all ${Animation.fast} ${Animation.spring}`,
      }}
      onTouchStart={() => {
        if (isClickable) {
          setIsPressed(true)
        }
      }}
      onTouchEnd={() => {
        if (isClickable) {
          setIsPressed(false)
        }
      }}
      onTouchCancel={() => {
        if (isClickable) {
          setIsPressed(false)
        }
      }}
      onMouseDown={() => {
        if (isClickable) {
          setIsPressed(true)
        }
      }}
      onMouseUp={() => {
        if (isClickable) {
          setIsPressed(false)
        }
      }}
      onMouseLeave={() => {
        if (isClickable) {
          setIsPressed(false)
        }
      }}
    >
      {/* Icon */}
      {icon && (
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: Spacing.md,
          flexShrink: 0,
        }}>
          {icon}
        </div>
      )}

      {/* Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0, // Allow text to truncate
      }}>
        <div style={{
          fontSize: Typography.fontSize.base,
          fontWeight: Typography.fontWeight.normal,
          color: OCAColors.label,
          lineHeight: '20px',
          marginBottom: subtitle ? '2px' : 0,
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{
            fontSize: Typography.fontSize.sm,
            color: OCAColors.secondaryLabel,
            lineHeight: '16px',
          }}>
            {subtitle}
          </div>
        )}
      </div>

      {/* Value and Status */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        marginLeft: Spacing.sm,
        flexShrink: 0,
        maxWidth: '50%', // Prevent value from taking too much space
      }}>
        {value && (
          <div style={{
            fontSize: Typography.fontSize.base,
            fontWeight: Typography.fontWeight.medium,
            color: OCAColors.label,
            lineHeight: '20px',
            marginBottom: status ? '2px' : 0,
            textAlign: 'right',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}>
            {value}
          </div>
        )}
        {status && (
          <div style={{
            fontSize: Typography.fontSize.sm,
            color: statusColor || OCAColors.secondaryLabel,
            lineHeight: '16px',
            textAlign: 'right',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}>
            {isAlarm && (
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: statusColor || OCAColors.danger,
                display: 'inline-block',
                animation: 'pulse 2s ease-in-out infinite',
              }} />
            )}
            {status}
          </div>
        )}
      </div>

      {/* Chevron */}
      {showChevron && isClickable && (
        <div style={{
          marginLeft: Spacing.xs,
          color: OCAColors.textTertiary,
          fontSize: '16px',
          fontWeight: Typography.fontWeight.medium,
        }}>
          ›
        </div>
      )}

      {/* Pulse animation for alarms */}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  )
}